import type { Article } from "../../../types/blog";

const article: Article = {
  slug: "haut-parleur-telephone-defaillant",
  silo: "telephone",
  h1: "Haut-parleur de téléphone défaillant : pas de son ou son de mauvaise qualité",
  metaTitle: "Haut-parleur téléphone en panne : causes et solutions",
  metaDescription: "Plus de son, son étouffé ou grésillant sur votre téléphone ? Découvrez les causes possibles et comment diagnostiquer un haut-parleur défaillant.",
  excerpt: "Musique inaudible, appels sans son, vidéos silencieuses : un haut-parleur défaillant a plusieurs origines possibles, pas toujours matérielles. Voici comment y voir clair.",
  publishedAt: "2026-09-07",
  readingMinutes: 5,
  relatedSlugs: ["micro-telephone-ne-fonctionne-plus", "telephone-tombe-dans-eau", "vitre-dos-telephone-casse", "wifi-bluetooth-telephone-ne-se-connecte-plus"],
  faq: [
    {
      question: "Pourquoi le son de mon téléphone est-il étouffé ?",
      answer: "Un son étouffé vient très souvent de poussière ou de peluches accumulées dans la grille du haut-parleur, qui bloquent partiellement la diffusion du son. Un nettoyage délicat suffit fréquemment à résoudre ce problème.",
    },
    {
      question: "Comment savoir si le problème vient du haut-parleur ou du volume ?",
      answer: "Vérifiez d'abord que le volume média n'est pas réduit et qu'aucun mode silencieux n'est activé. Testez ensuite avec plusieurs types de sons (musique, vidéo, appel, alarme) pour voir si le problème concerne un seul type de son ou l'ensemble.",
    },
    {
      question: "Un dégât liquide peut-il abîmer le haut-parleur sans que le téléphone soit tombé dans l'eau ?",
      answer: "Oui, de la transpiration, de la pluie fine, ou de l'humidité ambiante prolongée peuvent suffire à endommager progressivement un haut-parleur, même sans immersion franche de l'appareil.",
    },
    {
      question: "Peut-on nettoyer soi-même le haut-parleur d'un téléphone ?",
      answer: "Un nettoyage léger avec une brosse anti-statique douce ou de l'air comprimé à faible pression est possible avec précaution. Évitez tout objet pointu ou métallique qui pourrait endommager la membrane du haut-parleur.",
    },
  ],
  content: [
    {
      type: "p",
      text: "Volume qui semble bloqué à un niveau bas, son grésillant, ou silence complet malgré un volume au maximum : un haut-parleur défaillant perturbe autant les appels que le visionnage de vidéos ou l'écoute de musique. Bonne nouvelle, la cause n'est pas systématiquement matérielle.",
    },
    {
      type: "h2",
      text: "Les causes possibles d'un haut-parleur défaillant",
    },
    {
      type: "ul",
      items: [
        "Poussière, peluches ou saleté accumulées dans la grille du haut-parleur",
        "Dégât lié à l'humidité, même sans immersion complète du téléphone",
        "Réglages logiciels : mode silencieux, volume média réduit, ou bug après une mise à jour",
        "Application spécifique qui gère mal l'audio, tandis que les autres sons fonctionnent normalement",
        "Membrane du haut-parleur endommagée suite à un choc ou une chute",
        "Connecteur interne du haut-parleur desserré après un démontage ou une chute",
      ],
    },
    {
      type: "h2",
      text: "Premiers réflexes à avoir",
    },
    {
      type: "p",
      text: "Avant de conclure à une panne matérielle, quelques vérifications rapides permettent d'éliminer les causes les plus simples.",
    },
    {
      type: "ol",
      items: [
        "Vérifiez que le mode silencieux ou \"Ne pas déranger\" n'est pas activé",
        "Augmentez le volume média, qui est indépendant du volume de sonnerie sur la plupart des téléphones",
        "Testez plusieurs sources sonores : musique, vidéo, alarme, appel téléphonique",
        "Redémarrez le téléphone pour éliminer un bug logiciel temporaire",
        "Inspectez visuellement la grille du haut-parleur à la recherche de poussière ou de saleté visible",
        "Vérifiez qu'aucun accessoire Bluetooth n'est connecté et ne détourne le son sans que vous le remarquiez",
      ],
    },
    {
      type: "callout",
      variant: "tip",
      title: "Testez avec plusieurs types de sons",
      text: "Si seuls les appels sont sans son mais que la musique fonctionne normalement (ou l'inverse), le téléphone possède probablement plusieurs haut-parleurs distincts et cela oriente le diagnostic vers un composant précis plutôt qu'une panne générale.",
    },
    {
      type: "h2",
      text: "Quand le son est lié à un accessoire Bluetooth",
    },
    {
      type: "p",
      text: "Si votre téléphone semble muet alors que le volume est correctement réglé, vérifiez qu'il n'est pas connecté à un casque, une enceinte ou une voiture équipée du Bluetooth, même hors de portée. Ce cas de figure est fréquent et n'a rien à voir avec une panne matérielle. Si vous rencontrez aussi des soucis de connexion Bluetooth récurrents, notre article sur le [Wifi et Bluetooth qui ne se connectent plus](slug:wifi-bluetooth-telephone-ne-se-connecte-plus) peut vous aider à approfondir le diagnostic.",
    },
    {
      type: "callout",
      variant: "warning",
      title: "Ne forcez jamais un nettoyage avec un objet pointu",
      text: "N'insérez jamais d'aiguille, de trombone ou tout autre objet métallique dans la grille du haut-parleur pour tenter de le déboucher. Vous risquez de percer la membrane et de transformer un simple encrassement en panne matérielle irréversible.",
    },
    {
      type: "h2",
      text: "Quand le problème est matériel",
    },
    {
      type: "p",
      text: "Si après ces vérifications le son reste absent, grésillant ou déformé sur tous les types de contenus, le haut-parleur lui-même est probablement en cause, souvent à la suite d'un contact avec l'humidité ou d'un choc. C'est particulièrement probable si le téléphone a été récemment exposé à l'eau ou est [tombé dans l'eau](slug:telephone-tombe-dans-eau).",
    },
    {
      type: "p",
      text: "Le remplacement d'un haut-parleur est une intervention courante qui ne nécessite généralement pas de changer l'ensemble de l'appareil. Nos techniciens testent systématiquement tous les haut-parleurs du téléphone (appel, média, éventuellement stéréo) après intervention pour s'assurer d'un fonctionnement complet.",
    },
  ],
};

export default article;
